import { useState, useEffect, useRef } from "react";
import { getCurrentProjectId, switchProject, type Project } from "../lib/project";

export function ProjectSelector({ projects }: { projects: Project[] }) {
  const [currentId, setCurrentId] = useState(() => getCurrentProjectId());
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync currentId when cookie changes (e.g. Layout auto-sets it)
  useEffect(() => {
    const cid = getCurrentProjectId();
    if (cid && cid !== currentId) {
      setCurrentId(cid);
    }
  }, [projects]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  if (projects.length === 0) return null;

  const sorted = [...projects].sort((a, b) => a.name.localeCompare(b.name));
  const filtered = search
    ? sorted.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()))
    : sorted;
  const current = projects.find((p) => p.id === currentId);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => { setOpen(!open); setSearch(""); }}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-left bg-stone-50 dark:bg-zinc-800/50 hover:bg-stone-100 dark:hover:bg-zinc-800 border border-stone-200/60 dark:border-zinc-700/60 transition-colors"
      >
        <svg className="w-4 h-4 text-stone-400 dark:text-zinc-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
        <span className="flex-1 truncate text-stone-700 dark:text-zinc-300 font-medium">
          {current?.name || `Project ${currentId}`}
        </span>
        <svg className={`w-4 h-4 text-stone-400 dark:text-zinc-600 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-zinc-900 rounded-xl shadow-xl border border-stone-200/60 dark:border-zinc-800/60 z-50">
          <div className="p-2 border-b border-stone-100 dark:border-zinc-800">
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search projects..."
              className="w-full px-2.5 py-1.5 text-sm bg-stone-50 dark:bg-zinc-800 border border-stone-200/60 dark:border-zinc-700 rounded-lg text-stone-900 dark:text-zinc-100 placeholder-stone-400 dark:placeholder-zinc-600 outline-none focus:border-orange-400 dark:focus:border-orange-500 transition-colors"
            />
          </div>

          <div className="py-1 max-h-60 overflow-y-auto scrollbar-hide">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-stone-400 dark:text-zinc-600">No matches</div>
            ) : (
              filtered.map((project) => (
                <button
                  key={project.id}
                  onClick={() => {
                    if (project.id !== currentId) {
                      switchProject(project.id);
                    }
                    setOpen(false);
                    setSearch("");
                  }}
                  className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                    project.id === currentId
                      ? "bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400 font-medium"
                      : "text-stone-700 dark:text-zinc-300 hover:bg-stone-50 dark:hover:bg-zinc-800"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="truncate">{project.name}</span>
                    {project.id === currentId && (
                      <svg className="w-4 h-4 flex-shrink-0 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
